"use client"

import { useState, useContext, useEffect, useMemo } from "react"
import { ErpContext } from "../../context/ErpContext.js"
import { formatInputWithSeparators, parseLocalizedNumber, getLocaleConfig } from "../../utils/numeros.js"

import {
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  IconButton,
  Autocomplete,
  Modal,
  Divider,
  InputAdornment,
  Card,
  CardContent,
  Alert,
  Snackbar,
  useTheme,
  alpha,
} from "@mui/material"

// Material UI Icons
import {
  Close as CloseIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  Delete as DeleteIcon,
  Payments as PaymentsIcon,
  LocationOn as LocationOnIcon,
} from "@mui/icons-material"

const ModalMedioDePago = ({ 
  open, 
  handleClose, 
  precioTotal = 0, 
  transaccionSeleccionada = null, 
  onConfirmarPago,
  isCuentaCorriente = false 
}) => {
  const { 
    getMedioDePagoIdTipoEspecifico, 
    medioDePagoIdTipoEspecifico, 
    tipoMedioDePago, 
    getTipoMedioDePago,
    getUbicacion,
    ubicaciones,
    formatoNumericoGlobal,
  } = useContext(ErpContext)

  const theme = useTheme()

  // Obtener configuración de locale (memoizado para evitar recálculos)
  const localeConfig = useMemo(
    () => getLocaleConfig(formatoNumericoGlobal || null),
    [formatoNumericoGlobal]
  )

  // Estados para medios de pago
  const [modalMedioDePagoSeleccionadoID, setModalMedioDePagoSeleccionadoID] = useState(null)
  const [modalTipoMedioDePagoSeleccionado, setModalTipoMedioDePagoSeleccionado] = useState(null)
  const [modalMedioDePagoSeleccionado, setModalMedioDePagoSeleccionado] = useState(null)
  const [modalMedioDePagoCantidad, setModalMedioDePagoCantidad] = useState(null)
  const [modalMedioDePagoCantidadDisplay, setModalMedioDePagoCantidadDisplay] = useState("") // Valor formateado para mostrar
  const [medioDePagoList, setMedioDePagoList] = useState([])
  const [cantidaMedioDePagoList, setCantidaMedioDePagoList] = useState(0)
  const [selectedTipoMedioDePago, setSelectedTipoMedioDePago] = useState(null)
  const [selectedMedioDePago, setSelectedMedioDePago] = useState([])
  const [openCollapse, setOpenCollapse] = useState(false)
  const [mostrarButtonModalMedioDePago, setMostrarButtonModalMedioDePago] = useState(false)
  const [medioDePagoEspecificoSeleccionadoModal, setMedioDePagoEspecificoSeleccionadoModal] = useState(null)
  const [descripcionPago, setDescripcionPago] = useState("")
  const [selectedUbicacionModal, setSelectedUbicacionModal] = useState(null)

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  })

  useEffect(() => {
    const fetchData = async () => {
      try {
        await getTipoMedioDePago()
        // console.log("Tipo medio de pago fetched:", tipoMedioDePago)
      } catch (error) {
        console.error("Error fetching tipo medio de pago:", error)
      }
    }

    fetchData()
  }, [])

  // Cargar ubicaciones si es cuenta corriente
  useEffect(() => {
    const fetchUbicaciones = async () => {
      if (isCuentaCorriente && open) {
        try {
          await getUbicacion()
        } catch (error) {
          console.error("Error fetching ubicaciones:", error)
        }
      }
    }

    fetchUbicaciones()
  }, [isCuentaCorriente, open])

  // Limpiar campos cuando se cierra el modal
  useEffect(() => {
    if (!open) {
      setModalMedioDePagoCantidadDisplay("")
      setModalMedioDePagoCantidad(null)
    }
  }, [open])

  // Establecer ubicación por defecto cuando se cargan las ubicaciones
  useEffect(() => {
    if (isCuentaCorriente && open && ubicaciones && ubicaciones.length > 0 && !selectedUbicacionModal) {
      // Seleccionar la ubicación central (id=1) o la primera disponible
      const defaultUbicacion = ubicaciones.find((u) => u.id === 1) || ubicaciones[0]
      if (defaultUbicacion) {
        setSelectedUbicacionModal(defaultUbicacion)
      }
    }
  }, [isCuentaCorriente, open, ubicaciones, selectedUbicacionModal])

  // Agregar nuevo useEffect para cargar medios específicos
  useEffect(() => {
    const fetchMediosEspecificos = async () => {
      if (modalTipoMedioDePagoSeleccionado?.id) {
        try {
          await getMedioDePagoIdTipoEspecifico(modalTipoMedioDePagoSeleccionado)
        } catch (error) {
          console.error("Error fetching medios específicos:", error)
        }
      }
    }

    fetchMediosEspecificos()
  }, [modalTipoMedioDePagoSeleccionado])

  // Función para agregar nuevo medio de pago
  const handleAddNuevoMedioDePago = () => {
    const parsedAmount = parseLocalizedNumber(modalMedioDePagoCantidadDisplay, localeConfig.locale)
    
    if (modalMedioDePagoSeleccionado && parsedAmount !== null && parsedAmount > 0) {
      const yaExiste = medioDePagoList.some((mp) => mp.id === modalMedioDePagoSeleccionadoID)
      if (yaExiste) {
        showSnackbar("Este medio de pago ya ha sido seleccionado", "warning")
        return
      }

      const newMedioDePago = {
        id: modalMedioDePagoSeleccionadoID,
        descripcion: modalMedioDePagoSeleccionado,
        monto: parsedAmount,
        tipoMedioDePago: modalTipoMedioDePagoSeleccionado.id,
      }

      const nuevaLista = [...medioDePagoList, newMedioDePago]
      setMedioDePagoList(nuevaLista)

      const updatedTotalMedioPagoList = nuevaLista.reduce((total, item) => total + item.monto, 0)
      setCantidaMedioDePagoList(updatedTotalMedioPagoList)

      setModalMedioDePagoSeleccionado("")
      setModalMedioDePagoSeleccionadoID(null)
      setModalMedioDePagoCantidad(null)
      setModalMedioDePagoCantidadDisplay("")

      showSnackbar("Medio de pago agregado", "success")
    } else {
      showSnackbar("Debe seleccionar un medio de pago y especificar un monto", "warning")
    }
  }

  // Función para mostrar snackbar
  const showSnackbar = (message, severity = "info") => {
    setSnackbar({
      open: true,
      message,
      severity,
    })
  }

  // Función para eliminar medio de pago
  const handleDeleteMedioDePago = (index, medioDePagoMonto) => {
    setCantidaMedioDePagoList((prevTotal) => prevTotal - medioDePagoMonto)
    setMedioDePagoList(medioDePagoList.filter((_, i) => i !== index))

    showSnackbar("Medio de pago eliminado", "info")
  }

  // Función para resetear completamente el modal
  const resetModal = () => {
    setModalMedioDePagoSeleccionadoID(null)
    setModalTipoMedioDePagoSeleccionado(null)
    setModalMedioDePagoSeleccionado(null)
    setModalMedioDePagoCantidad(null)
    setMedioDePagoList([])
    setCantidaMedioDePagoList(0)
    setSelectedTipoMedioDePago(null)
    setSelectedMedioDePago([])
    setOpenCollapse(false)
    setMostrarButtonModalMedioDePago(false)
    setMedioDePagoEspecificoSeleccionadoModal(null)
    setDescripcionPago("")
    // Resetear ubicación solo si es cuenta corriente
    if (isCuentaCorriente) {
      if (ubicaciones && ubicaciones.length > 0) {
        const defaultUbicacion = ubicaciones.find((u) => u.id === 1) || ubicaciones[0]
        setSelectedUbicacionModal(defaultUbicacion || null)
      } else {
        setSelectedUbicacionModal(null)
      }
    }
  }

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 500,
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 3,
          borderRadius: 2,
          maxHeight: "90vh",
          overflow: "auto",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h6" sx={{ display: "flex", alignItems: "center" }}>
            <PaymentsIcon sx={{ mr: 1, color: "rgb(244, 147, 14)" }} />
            Selección de Medios de Pago
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Selector de ubicación solo para cuenta corriente */}
        {isCuentaCorriente && (
          <Box sx={{ mb: 2 }}>
            <Autocomplete
              size="small"
              fullWidth
              options={ubicaciones || []}
              getOptionLabel={(option) => option.descripcion || ""}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              value={selectedUbicacionModal}
              onChange={(event, newValue) => {
                setSelectedUbicacionModal(newValue)
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Ubicación"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <LocationOnIcon sx={{ ml: 1, mr: 0.5, color: theme.palette.primary.main }} />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Box>
        )}

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={6}>
            <Autocomplete
              size="small"
              fullWidth
              options={tipoMedioDePago}
              getOptionLabel={(option) => option.descripcion || ""}
              value={modalTipoMedioDePagoSeleccionado}
              onChange={(event, newValue) => setModalTipoMedioDePagoSeleccionado(newValue)}
              renderInput={(params) => <TextField {...params} label="Tipo de medio de pago" />}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Autocomplete
              size="small"
              fullWidth
              options={medioDePagoIdTipoEspecifico.map((it) => ({
                label: it.descripcion,
                id: it.id,
              }))}
              value={modalMedioDePagoSeleccionado}
              onChange={(event, newValue) => {
                if (newValue) {
                  setModalMedioDePagoSeleccionado(newValue.label)
                  setModalMedioDePagoSeleccionadoID(newValue.id)
                } else {
                  setModalMedioDePagoSeleccionado("")
                  setModalMedioDePagoSeleccionadoID(null)
                }
              }}
              renderInput={(params) => <TextField {...params} label="Medio de Pago" />}
            />
          </Grid>
        </Grid>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mb: 3,
            gap: 1,
            backgroundColor: "transparent",
          }}
        >
          <TextField
            label="Monto a pagar"
            size="small"
            fullWidth
            type="text"
            value={modalMedioDePagoCantidadDisplay}
            onChange={(e) => {
              const inputValue = e.target.value
              const { locale, decimalSeparator } = localeConfig

              if (inputValue === "") {
                setModalMedioDePagoCantidadDisplay("")
                setModalMedioDePagoCantidad(null)
                return
              }

              const parsedValue = parseLocalizedNumber(inputValue, locale)

              if (parsedValue === null && inputValue.trim() !== "") {
                return // Keep previous valid input if current is unparseable
              }

              let formattedValue = parsedValue !== null
                ? formatInputWithSeparators(parsedValue, locale)
                : ""

              const hasDecimalSeparator = inputValue.includes(decimalSeparator)
              const endsWithDecimal = inputValue.endsWith(decimalSeparator)
              const [, fractionalPartOriginal] = inputValue.split(decimalSeparator)

              // Special handling for decimal separator at the end or during fractional input
              if (endsWithDecimal && !formattedValue.endsWith(decimalSeparator)) {
                formattedValue += decimalSeparator
              } else if (hasDecimalSeparator && fractionalPartOriginal !== undefined) {
                const cleanedFraction = fractionalPartOriginal.replace(/\D/g, "").slice(0, 6)
                const integerPart = formattedValue.split(decimalSeparator)[0]
                formattedValue = `${integerPart}${decimalSeparator}${cleanedFraction}`
              }

              setModalMedioDePagoCantidadDisplay(formattedValue)
              setModalMedioDePagoCantidad(parsedValue)

              // Keep cursor at the end
              requestAnimationFrame(() => {
                const input = e.target
                if (input) {
                  input.setSelectionRange(formattedValue.length, formattedValue.length)
                }
              })
            }}
            onBlur={(e) => {
              const { locale } = localeConfig
              const parsed = parseLocalizedNumber(e.target.value, locale)
              if (parsed !== null && parsed >= 0) {
                const formatted = formatInputWithSeparators(parsed, locale)
                setModalMedioDePagoCantidadDisplay(formatted)
                setModalMedioDePagoCantidad(parsed)
              } else if (e.target.value === "") {
                setModalMedioDePagoCantidadDisplay("")
                setModalMedioDePagoCantidad(null)
              }
            }}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            inputProps={{
              inputMode: "decimal",
              autoComplete: "off",
            }}
          />
          <Button variant="contained" onClick={handleAddNuevoMedioDePago} startIcon={<AddCircleOutlineIcon />}>
            Agregar
          </Button>
        </Box>

        <Card variant="outlined" sx={{ mb: 3, bgcolor: alpha(theme.palette.primary.light, 0.05) }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 1 }}>
              Resumen de Pagos
            </Typography>

            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
              <Typography variant="body1">Total a pagar:</Typography>
              <Typography variant="body1" fontWeight="bold">
                {new Intl.NumberFormat("es-AR", {
                  style: "currency",
                  currency: "ARS",
                }).format(precioTotal)}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
              <Typography variant="body1">Total medios de pago:</Typography>
              <Typography variant="body1" fontWeight="bold">
                {new Intl.NumberFormat("es-AR", {
                  style: "currency",
                  currency: "ARS",
                }).format(cantidaMedioDePagoList)}
              </Typography>
            </Box>

            <Divider sx={{ my: 1 }} />

            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body1">Diferencia:</Typography>
              <Typography
                variant="body1"
                fontWeight="bold"
                color={precioTotal === cantidaMedioDePagoList ? "success.main" : "error.main"}
              >
                {new Intl.NumberFormat("es-AR", {
                  style: "currency",
                  currency: "ARS",
                }).format(precioTotal - cantidaMedioDePagoList)}
              </Typography>
            </Box>

            {precioTotal !== cantidaMedioDePagoList && cantidaMedioDePagoList > 0 && (
              <Alert severity={cantidaMedioDePagoList < precioTotal ? "info" : "warning"} sx={{ mt: 2 }}>
                {cantidaMedioDePagoList < precioTotal
                  ? `Pago parcial: Se pagará $${cantidaMedioDePagoList.toLocaleString("es-AR")} de $${precioTotal.toLocaleString("es-AR")}. Saldo restante: $${(precioTotal - cantidaMedioDePagoList).toLocaleString("es-AR")}`
                  : "El total de medios de pago es mayor al saldo a pagar"}
              </Alert>
            )}
          </CardContent>
        </Card>

        <Box sx={{ mb: 3 }}>
          <TextField
            label="Descripción del pago (opcional)"
            multiline
            rows={3}
            fullWidth
            value={descripcionPago}
            onChange={(e) => setDescripcionPago(e.target.value)}
            placeholder="Ej: Pago de factura #123, Abono a cuenta, Cancelación de deuda, etc."
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
              },
            }}
            helperText="Agregue una descripción para identificar este pago"
          />
        </Box>

        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Medios de Pago Seleccionados
        </Typography>

        {medioDePagoList.length > 0 ? (
          <Box sx={{ maxHeight: 200, overflow: "auto", mb: 2 }}>
            {medioDePagoList.map((it, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  p: 1,
                  mb: 1,
                  borderRadius: 1,
                  bgcolor: alpha(theme.palette.background.default, 0.7),
                  border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                }}
              >
                <Typography variant="body2">{it.descripcion}</Typography>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography variant="body2" sx={{ mr: 1 }}>
                    {new Intl.NumberFormat("es-AR", {
                      style: "currency",
                      currency: "ARS",
                    }).format(it.monto)}
                  </Typography>
                  <IconButton size="small" color="error" onClick={() => handleDeleteMedioDePago(index, it.monto)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: "center", py: 2 }}>
            No hay medios de pago seleccionados
          </Typography>
        )}

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button variant="outlined" onClick={handleClose}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (onConfirmarPago) {
                // Si es cuenta corriente, incluir la ubicación seleccionada
                if (isCuentaCorriente) {
                  onConfirmarPago(
                    medioDePagoList,
                    cantidaMedioDePagoList,
                    transaccionSeleccionada,
                    descripcionPago,
                    selectedUbicacionModal
                  )
                } else {
                  // Para otros usos, mantener la firma original
                  onConfirmarPago(medioDePagoList, cantidaMedioDePagoList, transaccionSeleccionada, descripcionPago)
                }
                resetModal() // Reset modal after confirming payment
              } else {
                handleClose()
              }
            }}
            disabled={
              medioDePagoList.length === 0 ||
              cantidaMedioDePagoList <= 0 ||
              (isCuentaCorriente && !selectedUbicacionModal)
            }
          >
            Confirmar
          </Button>
        </Box>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Modal>
  )
}

export default ModalMedioDePago
